import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VersionHistory = ({ versions, onRestore, onViewVersion, currentVersion }) => {
  const [expandedVersion, setExpandedVersion] = useState(null);

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChangesSummary = (changes) => {
    const changeTypes = Object.keys(changes);
    if (changeTypes?.length === 0) return 'No changes';
    if (changeTypes?.length === 1) return `Updated ${changeTypes?.[0]}`;
    return `Updated ${changeTypes?.length} fields`;
  };

  const getVersionIcon = (version) => {
    if (version?.id === currentVersion) return 'CheckCircle';
    if (version?.status === 'published') return 'Globe';
    return 'FileText';
  };

  const getVersionColor = (version) => {
    if (version?.id === currentVersion) return 'text-success';
    if (version?.status === 'published') return 'text-primary';
    return 'text-muted-foreground';
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-subtle h-full">
      <div className="p-4 border-b border-border">
        <h3 className="text-lg font-medium text-foreground flex items-center space-x-2">
          <Icon name="History" size={20} />
          <span>Version History</span>
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Track changes and restore previous versions
        </p>
      </div>
      <div className="overflow-y-auto max-h-[calc(100vh-200px)]">
        {versions?.length === 0 ? (
          <div className="p-6 text-center">
            <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No version history available</p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {versions?.map((version) => (
              <div
                key={version?.id}
                className={`border border-border rounded-lg p-4 transition-micro hover:bg-muted/50 ${
                  version?.id === currentVersion ? 'bg-success/5 border-success/20' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <Icon 
                      name={getVersionIcon(version)} 
                      size={20} 
                      className={getVersionColor(version)} 
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="text-sm font-medium text-foreground">
                          Version {version?.version}
                        </h4>
                        {version?.id === currentVersion && (
                          <span className="px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-md">
                            Current
                          </span>
                        )}
                        <span className={`px-2 py-1 text-xs font-medium rounded-md ${
                          version?.status === 'published' ?'bg-primary/10 text-primary' :'bg-muted text-muted-foreground'
                        }`}>
                          {version?.status}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        {formatDate(version?.createdAt)} by {version?.author}
                      </p>
                      <p className="text-sm text-foreground mb-2">
                        {getChangesSummary(version?.changes)}
                      </p>
                      {version?.description && (
                        <p className="text-sm text-muted-foreground">
                          {version?.description}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedVersion(
                        expandedVersion === version?.id ? null : version?.id
                      )}
                      iconName={expandedVersion === version?.id ? "ChevronUp" : "ChevronDown"}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onViewVersion(version)}
                      iconName="Eye"
                    />
                    {version?.id !== currentVersion && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRestore(version)}
                        iconName="RotateCcw"
                      />
                    )}
                  </div>
                </div>

                {expandedVersion === version?.id && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <h5 className="text-sm font-medium text-foreground mb-3">Changes Made:</h5>
                    <div className="space-y-2">
                      {Object.entries(version?.changes)?.map(([field, change]) => (
                        <div key={field} className="bg-muted/50 rounded-md p-3">
                          <div className="flex items-center space-x-2 mb-2">
                            <Icon name="Edit3" size={14} className="text-muted-foreground" />
                            <span className="text-sm font-medium text-foreground capitalize">
                              {field?.replace(/([A-Z])/g, ' $1')?.trim()}
                            </span>
                          </div>
                          <div className="text-xs space-y-1">
                            {change?.from && (
                              <div className="flex items-start space-x-2">
                                <span className="text-destructive font-medium">From:</span>
                                <span className="text-muted-foreground flex-1 break-words">
                                  {typeof change?.from === 'object' 
                                    ? JSON.stringify(change?.from, null, 2) 
                                    : change?.from}
                                </span>
                              </div>
                            )}
                            <div className="flex items-start space-x-2">
                              <span className="text-success font-medium">To:</span>
                              <span className="text-foreground flex-1 break-words">
                                {typeof change?.to === 'object' 
                                  ? JSON.stringify(change?.to, null, 2) 
                                  : change?.to}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VersionHistory;